<script setup>
import { Head, Link, useForm, usePage } from "@inertiajs/vue3";
import { AuthLayout } from "@/Components/blocks/auth-layout";
import { Button } from "@/Components/ui/button";
import { Input } from "@/Components/ui/input";
import { Label } from "@/Components/ui/label";
import { Checkbox } from "@/Components/ui/checkbox";
import { Alert, AlertDescription } from "@/Components/ui/alert";
import { Info } from "lucide-vue-next";
import { inject } from "vue";

const props = defineProps({
    canResetPassword: Boolean,
    status: String,
});

const form = useForm({
    email: "",
    password: "",
    remember: false,
});

const page = usePage();
const analytics = inject("analytics");

const submit = () => {
    form.transform((data) => ({
        ...data,
        remember: form.remember ? "on" : "",
    })).post(route("login"), {
        onFinish: () => {
            const { id, email, name, isMemberOfATeam, current_team_id } =
                page.props.auth.user;

            analytics.identify({
                id,
                email,
                name,
                isMemberOfATeam,
                current_team_id,
                first_visited_url: window.location.href,
            });
            form.reset("password");
        },
    });
};
</script>

<template>
    <Head title="Login" />
    <AuthLayout>
        <div class="space-y-2 text-center">
            <h1 class="text-3xl font-bold">Login</h1>
            <p class="text-gray-500">
                Welcome back to {{ page.props.appName }}. Enter your login
                details below to get started.
            </p>
        </div>

        <Alert v-if="status">
            <Info class="h-4 w-4" />
            <AlertDescription>
                {{ status }}
            </AlertDescription>
        </Alert>

        <form @submit.prevent="submit" class="space-y-4">
            <div class="space-y-2">
                <Label for="email">Email</Label>
                <Input
                    id="email"
                    v-model="form.email"
                    placeholder="name@example.com"
                    type="email"
                    required
                    autofocus
                    autocomplete="username"
                    :errors="form.errors.email"
                    :disabled="form.processing"
                />
            </div>
            <div class="space-y-2">
                <Label for="password">Password</Label>
                <Input
                    id="password"
                    v-model="form.password"
                    required
                    type="password"
                    autocomplete="current-password"
                    :errors="form.errors.password"
                    :disabled="form.processing"
                />
            </div>
            <div class="flex items-center space-x-2">
                <Checkbox
                    id="remember"
                    v-model="form.remember"
                    :disabled="form.processing"
                />
                <Label for="remember" class="text-sm font-normal">
                    Remember me
                </Label>
            </div>
            <Button class="w-full" type="submit" :loading="form.processing">
                Sign In
            </Button>
        </form>

        <div class="text-center text-sm">
            <Link
                :href="route('password.request')"
                class="underline"
                :disabled="form.processing"
            >
                Forgot your password?
            </Link>
        </div>

        <div class="text-center text-sm text-gray-500">
            Don't have an account?
            <Link
                :href="route('register')"
                class="underline"
                :disabled="form.processing"
            >
                Register
            </Link>
        </div>
    </AuthLayout>
</template>
